import { z } from "zod";

// ---------------------------------------------------------------------------
// Envelope
// ---------------------------------------------------------------------------

export const ErrorBodySchema = z.object({
	error: z.object({
		code: z.string(),
		message: z.string(),
		/** Stable machine-readable cause. See ReasonCodes. */
		reason: z.string().optional(),
		details: z.unknown().optional(),
	}),
});
export type ErrorBody = z.infer<typeof ErrorBodySchema>;

/** Transport-level error codes. Mapped 1:1 onto HTTP status by the services. */
export const ErrorCodes = {
	UNAUTHORIZED: "UNAUTHORIZED",
	FORBIDDEN: "FORBIDDEN",
	NOT_FOUND: "NOT_FOUND",
	VALIDATION_ERROR: "VALIDATION_ERROR",
	CONFLICT: "CONFLICT",
	PRECONDITION_FAILED: "PRECONDITION_FAILED",
	UNPROCESSABLE: "UNPROCESSABLE",
	RATE_LIMITED: "RATE_LIMITED",
	UPSTREAM_ERROR: "UPSTREAM_ERROR",
	INTERNAL: "INTERNAL",
} as const;
export type ErrorCode = (typeof ErrorCodes)[keyof typeof ErrorCodes];

/**
 * Domain reason codes. These are the stable vocabulary the Admin UI and
 * Agentry render; `ErrorCodes` only says which HTTP shape was used.
 */
export const ReasonCodes = {
	// auth / tenancy
	INVALID_CREDENTIALS: "INVALID_CREDENTIALS",
	TOKEN_INVALID: "TOKEN_INVALID",
	TOKEN_EXPIRED: "TOKEN_EXPIRED",
	ROLE_NOT_PERMITTED: "ROLE_NOT_PERMITTED",
	TENANT_MISMATCH: "TENANT_MISMATCH",
	EMAIL_ALREADY_REGISTERED: "EMAIL_ALREADY_REGISTERED",
	// provider / credential
	PROVIDER_NOT_FOUND: "PROVIDER_NOT_FOUND",
	MODEL_NOT_FOUND: "MODEL_NOT_FOUND",
	SECRET_REFERENCE_UNRESOLVED: "SECRET_REFERENCE_UNRESOLVED",
	SECRET_LITERAL_REJECTED: "SECRET_LITERAL_REJECTED",
	PROVIDER_POLICY_DENIED: "PROVIDER_POLICY_DENIED",
	PROVIDER_POLICY_UNVERIFIED: "PROVIDER_POLICY_UNVERIFIED",
	AUTH_PROFILE_DISABLED: "AUTH_PROFILE_DISABLED",
	AUTH_PROFILE_EXPIRED: "AUTH_PROFILE_EXPIRED",
	CREDENTIAL_REFRESH_REQUIRED: "CREDENTIAL_REFRESH_REQUIRED",
	NO_AUTH_CANDIDATE: "NO_AUTH_CANDIDATE",
	ASSIGNMENT_DENIED: "ASSIGNMENT_DENIED",
	QUOTA_EXHAUSTED: "QUOTA_EXHAUSTED",
	CONCURRENCY_EXHAUSTED: "CONCURRENCY_EXHAUSTED",
	REQUIRED_ROUTE_UNAVAILABLE: "REQUIRED_ROUTE_UNAVAILABLE",
	CROSS_PROVIDER_FALLBACK_DENIED: "CROSS_PROVIDER_FALLBACK_DENIED",
	CROSS_PROVIDER_FALLBACK_APPROVAL_REQUIRED: "CROSS_PROVIDER_FALLBACK_APPROVAL_REQUIRED",
	// workflow
	EXECUTION_TERMINAL: "EXECUTION_TERMINAL",
	STATE_VERSION_CONFLICT: "STATE_VERSION_CONFLICT",
	IDEMPOTENCY_KEY_REPLAY: "IDEMPOTENCY_KEY_REPLAY",
	IDEMPOTENCY_KEY_MISMATCH: "IDEMPOTENCY_KEY_MISMATCH",
	WAIT_HANDLE_RESOLVED: "WAIT_HANDLE_RESOLVED",
	WAIT_HANDLE_EXPIRED: "WAIT_HANDLE_EXPIRED",
	RESUME_TOKEN_INVALID: "RESUME_TOKEN_INVALID",
	NODE_NOT_RESUMABLE: "NODE_NOT_RESUMABLE",
	DEFINITION_NOT_PUBLISHED: "DEFINITION_NOT_PUBLISHED",
	IDEMPOTENCY_CONFLICT: "IDEMPOTENCY_CONFLICT",
	IDEMPOTENCY_IN_PROGRESS: "IDEMPOTENCY_IN_PROGRESS",
	WORKFLOW_START_FAILED: "WORKFLOW_START_FAILED",
	// artifact / storage
	ARTIFACT_NOT_FOUND: "ARTIFACT_NOT_FOUND",
	CHECKSUM_MISMATCH: "CHECKSUM_MISMATCH",
	OWNERSHIP_TRANSFER_NOT_PERMITTED: "OWNERSHIP_TRANSFER_NOT_PERMITTED",
	SECRET_MATERIAL_DETECTED: "SECRET_MATERIAL_DETECTED",
	// generic
	NOT_IMPLEMENTED: "NOT_IMPLEMENTED",
	UNEXPECTED: "UNEXPECTED",
} as const;
export type ReasonCode = (typeof ReasonCodes)[keyof typeof ReasonCodes];

// ---------------------------------------------------------------------------
// Shared primitives
// ---------------------------------------------------------------------------

export const IdSchema = z.string().min(1).max(128);
export const IsoDateTimeSchema = z.string().min(1);

export const PageQuerySchema = z.object({
	limit: z.coerce.number().int().min(1).max(200).default(50),
	offset: z.coerce.number().int().min(0).default(0),
});
export type PageQuery = z.infer<typeof PageQuerySchema>;

export function pageSchema<T extends z.ZodTypeAny>(item: T) {
	return z.object({
		items: z.array(item),
		total: z.number().int().min(0),
		limit: z.number().int().min(1),
		offset: z.number().int().min(0),
	});
}

export type Page<T> = {
	items: T[];
	total: number;
	limit: number;
	offset: number;
};

/** Every async command response carries this so the caller can poll/subscribe. */
export const AcceptedOperationSchema = z.object({
	operationId: IdSchema,
	status: z.enum(["ACCEPTED", "RUNNING", "SUCCEEDED", "FAILED"]),
	resourceType: z.string(),
	resourceId: IdSchema,
});
export type AcceptedOperation = z.infer<typeof AcceptedOperationSchema>;

/** Header names used across every command endpoint. */
export const Headers = {
	IDEMPOTENCY_KEY: "idempotency-key",
	EXPECTED_STATE_VERSION: "if-match-state-version",
	REASON: "x-zerovia-reason",
	TRACE_ID: "x-zerovia-trace-id",
	CONTRACT_VERSION: "x-zerovia-contract-version",
} as const;

export const ActorTypeSchema = z.enum(["USER", "AGENT", "SYSTEM", "PLATFORM_ADMIN"]);
export type ActorType = z.infer<typeof ActorTypeSchema>;

export const ActorSchema = z.object({
	type: ActorTypeSchema,
	id: IdSchema,
	displayName: z.string().optional(),
});
export type Actor = z.infer<typeof ActorSchema>;

export const HealthResponseSchema = z.object({
	ok: z.boolean(),
	service: z.string(),
	contractVersion: z.string(),
	time: IsoDateTimeSchema,
});
export type HealthResponse = z.infer<typeof HealthResponseSchema>;
